/**
 * Calculate model comparison statistics from comparisons data
 */
export const calculateComparisonStats = (comparisons) => {
  if (!comparisons || comparisons.length === 0) {
    return {
      total_comparisons: 0,
      successful_comparisons: 0,
      success_rate: 0,
      avg_latency_ms: 0,
      total_tokens: 0,
      total_cost: 0
    };
  }

  const successfulComparisons = comparisons.filter(c => 
    c.status_1 === 'success' && c.status_2 === 'success'
  );
  
  // Calculate total latency (sum of both models)
  const totalLatency = comparisons.reduce((sum, c) => 
    sum + (c.latency_ms_1 || 0) + (c.latency_ms_2 || 0), 0
  );
  
  // Calculate total tokens (sum of both models)
  const totalTokens = comparisons.reduce((sum, c) => {
    const tokens1 = c.token_usage_1?.total_tokens || 0;
    const tokens2 = c.token_usage_2?.total_tokens || 0;
    return sum + tokens1 + tokens2;
  }, 0);
  
  // Calculate total cost (sum of both models)
  const totalCost = comparisons.reduce((sum, c) => 
    sum + (c.estimated_cost_1 || 0) + (c.estimated_cost_2 || 0), 0
  );

  return {
    total_comparisons: comparisons.length,
    successful_comparisons: successfulComparisons.length,
    success_rate: comparisons.length > 0 ? (successfulComparisons.length / comparisons.length) * 100 : 0,
    avg_latency_ms: comparisons.length > 0 ? totalLatency / (comparisons.length * 2) : 0,
    total_tokens: totalTokens,
    total_cost: totalCost
  };
};

/**
 * Calculate model comparison breakdown (which models are being compared)
 */
export const calculateComparisonBreakdown = (comparisons) => {
  if (!comparisons || comparisons.length === 0) {
    return {
      pairs: []
    };
  }

  const pairMap = new Map();

  comparisons.forEach(comp => {
    // Create a consistent key for the model pair (sorted alphabetically)
    const models = [comp.model_1, comp.model_2].sort();
    const pairKey = `${models[0]}|||${models[1]}`;

    if (!pairMap.has(pairKey)) {
      pairMap.set(pairKey, {
        model_1: comp.model_1,
        model_2: comp.model_2,
        count: 0,
        latencies_1: [],
        latencies_2: [],
        costs_1: [],
        costs_2: []
      });
    }

    const pair = pairMap.get(pairKey);
    pair.count++;
    
    // Collect latencies and costs for averaging
    if (comp.latency_ms_1) pair.latencies_1.push(comp.latency_ms_1);
    if (comp.latency_ms_2) pair.latencies_2.push(comp.latency_ms_2);
    if (comp.estimated_cost_1) pair.costs_1.push(comp.estimated_cost_1);
    if (comp.estimated_cost_2) pair.costs_2.push(comp.estimated_cost_2);
  });

  // Calculate averages and format results
  const pairs = Array.from(pairMap.values()).map(pair => {
    const avgLatency1 = pair.latencies_1.length > 0 
      ? pair.latencies_1.reduce((a, b) => a + b, 0) / pair.latencies_1.length 
      : 0;
    const avgLatency2 = pair.latencies_2.length > 0 
      ? pair.latencies_2.reduce((a, b) => a + b, 0) / pair.latencies_2.length 
      : 0;
    const avgCost1 = pair.costs_1.length > 0 
      ? pair.costs_1.reduce((a, b) => a + b, 0) / pair.costs_1.length 
      : 0;
    const avgCost2 = pair.costs_2.length > 0 
      ? pair.costs_2.reduce((a, b) => a + b, 0) / pair.costs_2.length 
      : 0;

    return {
      model_1: pair.model_1,
      model_2: pair.model_2,
      count: pair.count,
      avg_latency_model_1: avgLatency1,
      avg_latency_model_2: avgLatency2,
      avg_cost_model_1: avgCost1,
      avg_cost_model_2: avgCost2
    };
  });

  // Sort by count (most compared pairs first)
  pairs.sort((a, b) => b.count - a.count);

  return {
    pairs
  };
};

